import { Component, Injector, OnInit, computed, forwardRef, input, model, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxSelectBoxComponent, DxSelectBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { LayoutControlComponent } from '../../internal/layout-control/layout-control.component';
import { ControlLabelComponent } from '../../internal/control-label/control-label.component';
import { DxTemplateModule } from 'devextreme-angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ControlSupportingTextComponent } from '../../internal/control-supporting-text/control-supporting-text.component';

@Component({
  selector: 'app-singleselectbox',
  templateUrl: './singleselectbox.component.html',
  styleUrls: ['./singleselectbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleselectboxComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LayoutControlComponent, ControlLabelComponent, DxSelectBoxModule, DxTemplateModule, NgTemplateOutlet, ControlSupportingTextComponent]
})
export class SingleselectboxComponent implements OnInit, ControlValueAccessor {
  private dx = viewChild.required<DxSelectBoxComponent>('dx');
  private ngControl: NgControl = null;

  caption = input.required<string>();
  helpText = input<string>('');
  value = model<unknown>(null);
  dataSource = input.required<unknown[]>();
  grouped = input<boolean>(false);
  valueExpr = input<string>(null);
  displayExpr = input<string>(null);
  searchExpr = input<string | string[]>(null);
  autoSort = input<boolean>(true);
  itemTemplate = input<unknown>(null);
  groupTemplate = input<unknown>(null);
  readonly = input<boolean>(false);
  disabled = model<boolean>(false);

  computedDataSource = computed(() => {
    const sortExpr = this.displayExpr() ?? this.valueExpr();
    return this.autoSort()
      ? this.dataSource()?.slice().sort((a, b) => {
          if (sortExpr) {
            const aValue = typeof a[sortExpr] === 'string' ? a[sortExpr] : String(a[sortExpr] ?? '');
            const bValue = typeof b[sortExpr] === 'string' ? b[sortExpr] : String(b[sortExpr] ?? '');
            return aValue.localeCompare(bValue);
          } else {
            const aStr = typeof a === 'string' ? a : a as string;
            const bStr = typeof b === 'string' ? b : b as string;
            return aStr.localeCompare(bStr);
          }
        })
      : this.dataSource();
  });

  computedSearchExpr = computed(() => {
    const searchValue = this.searchExpr() ?? this.displayExpr();
    return searchValue;
  });

  constructor(
    private injector: Injector
  ) { }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
  }

  get control(): AbstractControl {
    return this.ngControl?.control;
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.onChange(e.value);
      this.value.set(e.value);
    }
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: unknown): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }
}
